import React, { useState, useRef, useCallback, useEffect } from 'react';
import './SalesLookup.css';

const API_URL = process.env.REACT_APP_API_URL || "http://localhost:8000";

const SALE_STATUSES = ['PAID', 'PARTIAL', 'OWING'];
const TODAY = new Date().toISOString().split('T')[0];

function StatusBadge({ status }) {
  if (!status) return <span className="status-badge unknown">N/A</span>;
  const cls = status === 'PAID' ? 'paid' : status === 'PARTIAL' ? 'partial' : 'owing';
  return <span className={`status-badge ${cls}`}>{status}</span>;
}

function OrderCard({ order, expandedRef, toggleExpand, updateStatus, setUpdateStatus, updateAmountPaid, setUpdateAmountPaid, updating, handleUpdate, updateMessage, onEdit }) {
  return (
    <div className={`order-card ${expandedRef === order.group_ref ? 'expanded' : ''}`}>
      <div
        className="order-card-header"
        onClick={() => toggleExpand(order.group_ref)}
      >
        <div className="order-card-summary">
          <span className="order-customer">{order.customer_name}</span>
          <span className="order-date">{order.sale_date}</span>
          <span className="order-items-count">
            {order.items.length} item{order.items.length !== 1 ? 's' : ''}
          </span>
        </div>
        <div className="order-card-right">
          <span className="order-total">{order.order_total.toFixed(2)}</span>
          <StatusBadge status={order.status} />
          <span className="expand-icon">{expandedRef === order.group_ref ? '\u25B2' : '\u25BC'}</span>
        </div>
      </div>

      {expandedRef === order.group_ref && (
        <div className="order-card-body">
          <div className="order-meta">
            {order.sale_channel && (
              <span className="meta-tag">Channel: {order.sale_channel}</span>
            )}
            {order.amount_paid != null && (
              <span className="meta-tag">Paid: {order.amount_paid.toFixed(2)}</span>
            )}
            <span className="meta-tag">Ref: {order.group_ref}</span>
          </div>

          {onEdit && (
            <div className="order-actions">
              <button
                type="button"
                className="edit-order-btn"
                onClick={() => onEdit(order)}
              >
                Edit Order
              </button>
            </div>
          )}

          <table className="order-items-table">
            <thead>
              <tr>
                <th>Product</th>
                <th>Qty</th>
                <th>Price</th>
                <th>Total</th>
              </tr>
            </thead>
            <tbody>
              {order.items.map(item => (
                <tr key={item.sale_id}>
                  <td>{item.product_name}</td>
                  <td>{item.quantity}</td>
                  <td>{(item.sale_price ?? item.unit_price).toFixed(2)}</td>
                  <td>{item.line_total.toFixed(2)}</td>
                </tr>
              ))}
            </tbody>
          </table>

          <div className="order-update-form">
            <h4>Update Payment Status</h4>
            <div className="update-form-row">
              <div className="update-form-group">
                <label>Status</label>
                <select
                  value={updateStatus}
                  onChange={e => {
                    setUpdateStatus(e.target.value);
                    if (e.target.value !== 'PARTIAL') setUpdateAmountPaid('');
                  }}
                >
                  <option value="">-- Select --</option>
                  {SALE_STATUSES.map(s => (
                    <option key={s} value={s}>{s}</option>
                  ))}
                </select>
              </div>
              {updateStatus === 'PARTIAL' && (
                <div className="update-form-group">
                  <label>Amount Paid</label>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    value={updateAmountPaid}
                    onChange={e => setUpdateAmountPaid(e.target.value)}
                    placeholder="0.00"
                    required
                  />
                </div>
              )}
              <button
                type="button"
                className="update-btn"
                disabled={!updateStatus || updating}
                onClick={() => handleUpdate(order.group_ref)}
              >
                {updating ? 'Updating...' : 'Update'}
              </button>
            </div>
            {updateMessage && (
              <div className={`lookup-message ${updateMessage.type}`}>
                {updateMessage.text}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function SalesLookup({ onEditOrder }) {
  // Search state
  const [customerName, setCustomerName] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [searching, setSearching] = useState(false);
  const [searchError, setSearchError] = useState(null);
  const [orders, setOrders] = useState(null);
  const [sections, setSections] = useState(null);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [appliedDateFrom, setAppliedDateFrom] = useState(null);
  const [appliedDateTo, setAppliedDateTo] = useState(null);

  // Recent orders
  const [recentOrders, setRecentOrders] = useState(null);
  const [recentLoading, setRecentLoading] = useState(true);

  // Expanded order
  const [expandedRef, setExpandedRef] = useState(null);

  // Update state
  const [updateStatus, setUpdateStatus] = useState('');
  const [updateAmountPaid, setUpdateAmountPaid] = useState('');
  const [updating, setUpdating] = useState(false);
  const [updateMessage, setUpdateMessage] = useState(null);

  // Customer typeahead state
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const debounceRef = useRef(null);
  const blurTimeoutRef = useRef(null);

  const fetchRecentOrders = useCallback(async () => {
    setRecentLoading(true);
    try {
      const res = await fetch(`${API_URL}/sales/outstanding`);
      if (res.ok) {
        const data = await res.json();
        setRecentOrders(data.orders || []);
      }
    } catch {
      // best-effort
    } finally {
      setRecentLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchRecentOrders();
  }, [fetchRecentOrders]);

  useEffect(() => {
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
      if (blurTimeoutRef.current) clearTimeout(blurTimeoutRef.current);
    };
  }, []);

  const fetchSuggestions = useCallback((query) => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (!query || query.length < 2) {
      setSuggestions([]);
      setShowSuggestions(false);
      return;
    }
    debounceRef.current = setTimeout(async () => {
      try {
        const params = new URLSearchParams({ q: query });
        const res = await fetch(`${API_URL}/sales/customers/suggest?${params}`);
        if (res.ok) {
          const data = await res.json();
          setSuggestions(data.customers || []);
          setShowSuggestions((data.customers || []).length > 0);
        }
      } catch {
        // best-effort
      }
    }, 300);
  }, []);

  const handleCustomerNameChange = (value) => {
    setCustomerName(value);
    fetchSuggestions(value);
  };

  const handleSelectSuggestion = (customer) => {
    setCustomerName(customer.name);
    setSuggestions([]);
    setShowSuggestions(false);
  };

  const handleNameBlur = () => {
    blurTimeoutRef.current = setTimeout(() => setShowSuggestions(false), 200);
  };

  const handleNameFocus = () => {
    if (suggestions.length > 0) setShowSuggestions(true);
  };

  const fetchPage = async (targetPage) => {
    setSearching(true);
    setSearchError(null);
    setExpandedRef(null);
    setUpdateMessage(null);

    try {
      const params = new URLSearchParams({ page: targetPage });
      if (customerName) params.set('customer_name', customerName);
      if (dateFrom) params.set('date_from', dateFrom);
      if (dateTo) params.set('date_to', dateTo);
      const response = await fetch(`${API_URL}/sales/search?${params}`);

      if (!response.ok) {
        const err = await response.json();
        throw new Error(err.detail || 'Search failed');
      }

      const data = await response.json();
      if (data.sections) {
        setSections(data.sections);
        setOrders(null);
      } else {
        setOrders(data.orders || []);
        setSections(null);
      }
      setPage(data.page || targetPage);
      setHasMore(data.has_more || false);
      setAppliedDateFrom(data.date_from || null);
      setAppliedDateTo(data.date_to || null);
    } catch (err) {
      setSearchError(err.message);
    } finally {
      setSearching(false);
    }
  };

  const handleSearch = async (e) => {
    e.preventDefault();
    if (!customerName && !dateFrom && !dateTo) {
      setSearchError('Enter a customer name and/or a date range');
      return;
    }
    setOrders(null);
    setSections(null);
    setPage(1);
    await fetchPage(1);
  };

  const findOrder = (groupRef) => {
    if (orders) return orders.find(o => o.group_ref === groupRef);
    if (sections) {
      for (const sec of sections) {
        const found = sec.orders.find(o => o.group_ref === groupRef);
        if (found) return found;
      }
    }
    if (recentOrders) return recentOrders.find(o => o.group_ref === groupRef);
    return null;
  };

  const toggleExpand = (groupRef) => {
    if (expandedRef === groupRef) {
      setExpandedRef(null);
    } else {
      setExpandedRef(groupRef);
      // Pre-fill update form with current status
      const order = findOrder(groupRef);
      if (order) {
        setUpdateStatus(order.status || '');
        setUpdateAmountPaid(order.amount_paid != null ? String(order.amount_paid) : '');
      }
      setUpdateMessage(null);
    }
  };

  const handleUpdate = async (groupRef) => {
    if (updateStatus === 'PARTIAL' && !updateAmountPaid) {
      setUpdateMessage({ type: 'error', text: 'Amount paid is required for PARTIAL status' });
      return;
    }

    setUpdating(true);
    setUpdateMessage(null);

    try {
      const payload = {
        status: updateStatus,
        amount_paid: updateStatus === 'PARTIAL' ? parseFloat(updateAmountPaid) : null,
      };

      const response = await fetch(`${API_URL}/sales/order/${encodeURIComponent(groupRef)}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        const err = await response.json();
        throw new Error(err.detail || 'Update failed');
      }

      const data = await response.json();

      // Optimistic update: reflect changes in local state
      const updateOrder = o =>
        o.group_ref === groupRef
          ? { ...o, status: updateStatus, amount_paid: payload.amount_paid }
          : o;

      if (orders) {
        setOrders(prev => prev.map(updateOrder));
      }
      if (sections) {
        setSections(prev =>
          prev.map(sec => ({
            ...sec,
            orders: sec.orders.map(updateOrder),
          }))
        );
      }

      setUpdateMessage({ type: 'success', text: data.message });
      fetchRecentOrders();
    } catch (err) {
      setUpdateMessage({ type: 'error', text: err.message });
    } finally {
      setUpdating(false);
    }
  };

  return (
    <div className="sales-lookup">
      <h2>Find Sale</h2>
      <p className="sales-lookup-subtitle">Search by customer name, date range, or both</p>

      <form className="lookup-search-form" onSubmit={handleSearch}>
        <div className="lookup-form-row">
          <div className="lookup-form-group suggestions-wrapper">
            <label htmlFor="lookupCustomer">Customer Name</label>
            <input
              id="lookupCustomer"
              type="text"
              value={customerName}
              onChange={e => handleCustomerNameChange(e.target.value)}
              onBlur={handleNameBlur}
              onFocus={handleNameFocus}
              placeholder="Search by name..."
              autoComplete="off"
            />
            {showSuggestions && suggestions.length > 0 && (
              <div className="suggestions-dropdown">
                {suggestions.map(c => (
                  <div
                    key={c.customer_id}
                    className="suggestion-item"
                    onMouseDown={() => handleSelectSuggestion(c)}
                  >
                    <span className="suggestion-name">{c.name}</span>
                    {c.phone && <span className="suggestion-phone">{c.phone}</span>}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
        <div className="lookup-form-row">
          <div className="lookup-form-group">
            <label htmlFor="lookupDateFrom">From</label>
            <input
              id="lookupDateFrom"
              type="date"
              value={dateFrom}
              max={TODAY}
              onChange={e => setDateFrom(e.target.value)}
            />
          </div>
          <div className="lookup-form-group">
            <label htmlFor="lookupDateTo">To</label>
            <input
              id="lookupDateTo"
              type="date"
              value={dateTo}
              max={TODAY}
              onChange={e => setDateTo(e.target.value)}
            />
          </div>
        </div>
        <button type="submit" className="lookup-search-btn" disabled={searching}>
          {searching ? 'Searching...' : 'Search'}
        </button>
      </form>

      {searchError && (
        <div className="lookup-message error">{searchError}</div>
      )}

      {/* Recent orders (shown when no search results yet) */}
      {orders === null && sections === null && !searchError && (
        <div className="recent-orders-section">
          <h3 className="recent-orders-heading">Outstanding Orders</h3>
          {recentLoading && <div className="lookup-message info">Loading...</div>}
          {!recentLoading && recentOrders && recentOrders.length === 0 && (
            <div className="lookup-message info">No outstanding orders found.</div>
          )}
          {!recentLoading && recentOrders && recentOrders.length > 0 && (
            <div className="lookup-results">
              {recentOrders.map(order => (
                <OrderCard
                  key={order.group_ref}
                  order={order}
                  expandedRef={expandedRef}
                  toggleExpand={toggleExpand}
                  updateStatus={updateStatus}
                  setUpdateStatus={setUpdateStatus}
                  updateAmountPaid={updateAmountPaid}
                  setUpdateAmountPaid={setUpdateAmountPaid}
                  updating={updating}
                  handleUpdate={handleUpdate}
                  updateMessage={updateMessage}
                  onEdit={onEditOrder}
                />
              ))}
            </div>
          )}
        </div>
      )}

      {appliedDateFrom && appliedDateTo && orders !== null && (
        <div className="lookup-date-hint">
          Showing results from {appliedDateFrom} to {appliedDateTo}
        </div>
      )}

      {/* Empty state */}
      {((orders !== null && orders.length === 0) || (sections !== null && sections.length === 0)) && (
        <div className="lookup-message info">No orders found. Try a different name or date range.</div>
      )}

      {/* Sectioned results (name-only search) */}
      {sections && sections.length > 0 && (
        <div className="lookup-results">
          {sections.map(section => (
            <div key={section.customer_id || section.customer_name} className="customer-section">
              <div className="customer-section-header">
                <span className="customer-section-name">{section.customer_name}</span>
                <span className="customer-section-count">{section.order_count} order{section.order_count !== 1 ? 's' : ''}</span>
              </div>
              {section.orders.map(order => (
                <OrderCard
                  key={order.group_ref}
                  order={order}
                  expandedRef={expandedRef}
                  toggleExpand={toggleExpand}
                  updateStatus={updateStatus}
                  setUpdateStatus={setUpdateStatus}
                  updateAmountPaid={updateAmountPaid}
                  setUpdateAmountPaid={setUpdateAmountPaid}
                  updating={updating}
                  handleUpdate={handleUpdate}
                  updateMessage={updateMessage}
                  onEdit={onEditOrder}
                />
              ))}
            </div>
          ))}
        </div>
      )}

      {/* Flat results (date search) */}
      {orders && orders.length > 0 && (
        <div className="lookup-results">
          {orders.map(order => (
            <OrderCard
              key={order.group_ref}
              order={order}
              expandedRef={expandedRef}
              toggleExpand={toggleExpand}
              updateStatus={updateStatus}
              setUpdateStatus={setUpdateStatus}
              updateAmountPaid={updateAmountPaid}
              setUpdateAmountPaid={setUpdateAmountPaid}
              updating={updating}
              handleUpdate={handleUpdate}
              updateMessage={updateMessage}
              onEdit={onEditOrder}
            />
          ))}

          {/* Pagination */}
          <div className="lookup-pagination">
            <button
              type="button"
              className="pagination-btn"
              disabled={page <= 1 || searching}
              onClick={() => fetchPage(page - 1)}
            >
              Previous
            </button>
            <span className="pagination-info">Page {page}</span>
            <button
              type="button"
              className="pagination-btn"
              disabled={!hasMore || searching}
              onClick={() => fetchPage(page + 1)}
            >
              Next
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export default SalesLookup;
